import React, { useState, useEffect } from "react";
import type { Recipe } from "../../types/Recipe";
import type { Filters } from "../FilterModal";
import FilterModal from "../FilterModal";
import { useFontenehus } from "../../context/FontenehusContext";
import { useModalContext } from "../../context/ModalContext";
import RecipeDetail from "../../pages/RecipeDetailPage";
import { createPortal } from "react-dom";

interface Props {
  allRecipes: Recipe[];
  menuRecipeIds: number[];
  selectedRecipeId: number | "";
  setSelectedRecipeId: (id: number | "") => void;
  onAddRecipe: () => void;
  buttonLabel?: string;
  recipeCategories: any;
  allergensList: any;
  ingredientsList: any;
}

const AddRecipeSection: React.FC<Props> = ({
  allRecipes,
  menuRecipeIds,
  selectedRecipeId,
  setSelectedRecipeId,
  onAddRecipe,
  buttonLabel = "➕ Legg til",
  recipeCategories,
  allergensList,
  ingredientsList,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");

  const selectedRecipe = allRecipes.find((r) => r.id === selectedRecipeId);

  const { fontenehusList } = useFontenehus();
  const { setIsAnyModalOpen } = useModalContext();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    categoryIds: [],
    fontenehusIds: [],
    allergenIds: [],
    ingredientIds: [],
  });

  const [showModalRecipe, setShowModalRecipe] = useState<number | undefined>(
    undefined,
  );

  const handleSelectRecipe = (recipeId: number) => {
    setSelectedRecipeId(recipeId);
    setShowModal(false);
    setSearch("");
  };

  useEffect(() => {
    setIsAnyModalOpen(showModal);
  }, [showModal, setIsAnyModalOpen]);

  const handleAddRecipe = () => {
    onAddRecipe();
    setSelectedRecipeId("");
  };

  // Filter recipes that aren't already suggested
  const availableRecipes = allRecipes.filter(
    (r) => !menuRecipeIds.some((s) => s === r.id),
  );

  const filteredRecipes = availableRecipes.filter((recipe) => {
    const matchesSearch = recipe.title
      .toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory =
      filters.categoryIds.length === 0 ||
      (recipe.categories ?? []).some((rc) =>
        filters.categoryIds.includes(rc.id),
      );

    const matchesFontenehus =
      filters.fontenehusIds.length === 0 ||
      filters.fontenehusIds.includes(recipe.fontenehusId);

    const matchesAllergens =
      filters.allergenIds.length === 0 ||
      !(recipe.allergens ?? []).some((a) => filters.allergenIds.includes(a.id));

    const matchesIngredients =
      filters.ingredientIds.length === 0 ||
      (recipe.ingredients ?? []).some((ri) =>
        filters.ingredientIds.includes(ri.id),
      );

    return (
      matchesSearch &&
      matchesCategory &&
      matchesFontenehus &&
      matchesAllergens &&
      matchesIngredients
    );
  });

  return (
    <div className="mt-5 flex justify-center w-full">
      <div className="flex items-center gap-3 w-full max-w-[800px]">
        {/* Selected recipe display */}
        <div className="relative flex-1 w-full">
          <div
            className="w-full h-full border border-gray-300 z-[45] rounded-md px-3 py-2 cursor-pointer bg-white text-left flex items-center justify-between"
            onClick={() => setShowModal(true)}
          >
            <span
              className={selectedRecipe ? "text-gray-800" : "text-gray-500"}
            >
              {selectedRecipe ? selectedRecipe.title : "Velg oppskrift"}
            </span>
          </div>
        </div>

        {/* Add button */}
        <button
          disabled={!selectedRecipeId}
          onClick={handleAddRecipe}
          className={`px-4 rounded-md font-semibold whitespace-nowrap transition h-[calc(2.5rem+8px)] ${
            selectedRecipeId
              ? "bg-orangeBrand hover:bg-orangeHover text-white"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {buttonLabel}
        </button>

        {/* Search Modal */}
        {showModal &&
          createPortal(
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-auto"
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
            >
              <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col shadow-lg">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">
                      Velg oppskrift til dagsmeny
                    </h2>
                    <button
                      onClick={() => {
                        setShowModal(false);
                        setSearch("");
                      }}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  {/* Search input */}
                  <input
                    type="text"
                    placeholder="Skriv for å søke..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:border-orangeBrand"
                  />
                </div>

                {/* Filter Modal */}
                <div className="flex justify-center gap-2 mb-3">
                  <button
                    onClick={() => setFiltersOpen(true)}
                    className={`relative text-sm font-medium px-2.5 py-1 rounded-md shadow transition flex items-center justify-center
      ${
        filters.categoryIds.length ||
        filters.fontenehusIds.length ||
        filters.allergenIds.length ||
        filters.ingredientIds.length
          ? "bg-orange-600 text-white"
          : "bg-orange-500 text-white hover:bg-orange-600"
      } 
    `}
                    style={{ width: "auto", minWidth: "50px" }}
                  >
                    Filtrer
                    {filters.categoryIds.length +
                      filters.fontenehusIds.length +
                      filters.allergenIds.length +
                      filters.ingredientIds.length >
                      0 && (
                      <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center text-xs font-semibold bg-white text-orange-600 rounded-full border border-orange-600">
                        {filters.categoryIds.length +
                          filters.fontenehusIds.length +
                          filters.allergenIds.length +
                          filters.ingredientIds.length}
                      </span>
                    )}
                  </button>
                </div>

                <FilterModal
                  isOpen={filtersOpen}
                  onClose={() => setFiltersOpen(false)}
                  categories={recipeCategories}
                  fontenehusList={fontenehusList}
                  allergens={allergensList}
                  ingredients={ingredientsList}
                  onApplyFilters={setFilters}
                />

                {/* Recipe list */}
                <div
                  className="flex-1 overflow-y-auto px-2"
                  style={{ maxHeight: "250px" }}
                >
                  {filteredRecipes.length > 0 ? (
                    filteredRecipes.map((recipe) => (
                      <div
                        key={recipe.id}
                        className="px-4 py-2 border-b border-gray-100 hover:bg-gray-50 active:bg-gray-100 cursor-pointer rounded-md"
                      >
                        <div className="font-medium text-gray-800">
                          {recipe.title}
                        </div>
                        {recipe.description && (
                          <div className="text-sm text-gray-600 mt-1">
                            {recipe.description}
                          </div>
                        )}
                        {/* NEW: View recipe button */}
                        <div className="flex justify-center mt-2 gap-x-2">
                          <button
                            onClick={() => {
                              setShowModalRecipe(recipe.id);
                            }}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm px-3 py-1 rounded-md"
                          >
                            📖 Se oppskrift
                          </button>

                          <button
                            onClick={() => handleSelectRecipe(recipe.id)}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm px-3 py-1 rounded-md"
                          >
                            📖 Legg til
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-4 text-center text-gray-500">
                      {search
                        ? "Ingen oppskrifter funnet"
                        : "Ingen tilgjengelige oppskrifter"}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => {
                      setSelectedRecipeId("");
                      setShowModal(false);
                      setSearch("");
                    }}
                    className="w-full py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                  >
                    {selectedRecipeId ? "Fjern valg" : "Lukk"}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
        {/* Recipe Modal Portal */}
        {showModalRecipe &&
          createPortal(
            <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
              <RecipeDetail
                isOpen={showModalRecipe != undefined}
                isModal
                recipeId={showModalRecipe}
                onClose={() => setShowModalRecipe(undefined)}
              />
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default AddRecipeSection;
